/**
 * critique-corpus.ts — the append-only JudgeCritiqueStore corpus discipline
 * (Epic gridline-marry-refinement Phase J5c).
 *
 * Design: docs/epics/active/gridline-marry-refinement/phase-J5-design-260817.md
 * §3.2 (persistence — genre-check) + §3.3 (lifecycle + partition).
 *
 * GENRE (A.9 / rule #22). An append-only, versioned corpus of human structural
 * judgements sitting next to the LANDED GT corpus (`test/gt-corpus/manifest.json`) —
 * one JSON file per critique, authored by the same file-download + repo-commit
 * workflow as GT, no server endpoint. This module holds the PURE corpus discipline
 * (stamped filename, default/promote partition, append-only helpers); the on-disk dir
 * is `test/critique-corpus/` and the append-only invariant is forward-policed by
 * `tools/lint/lint-critique-corpus-append-only.py`.
 *
 * LIFECYCLE (§3.3):
 *   draft (in-editor) -> exported (downloaded stamped JSON) -> committed (repo corpus)
 *   -> consumed (few-shot builder / GT-eval harness / algorithm regression).
 *
 * APPEND-ONLY (Phase-11 §3.2): a past record is NEVER rewritten; a superseding
 * correction is a NEW record. Provenance is the training history. `promoteToFrozen`
 * therefore returns a NEW record value — it never mutates its input.
 *
 * Pure + Node-importable: type-only import of the critique types; no DOM globals.
 *
 * @module editor/critique-corpus
 */
/**
 * The repo corpus directory (repo-relative). Sibling of the landed GT corpus
 * (`test/gt-corpus/`) — same corpus class, kept together (G-J5-Q6 intent: "next to
 * the GT corpus"; the landed GT corpus lives in the main repo, so the critique corpus
 * does too).
 */
export const CRITIQUE_CORPUS_DIR = "test/critique-corpus";
/** The default partition for a freshly-captured critique (§3.3: promotion is explicit). */
export const DEFAULT_PARTITION = "train";
/** Two-digit zero-pad. */
function pad2(n) {
    return String(n).padStart(2, "0");
}
/**
 * Stamped critique filename: `<base>-critique-YYMMDD.HHMMSS.json` (rule #6 — a
 * timestamp so successive exports don't silently overwrite). Mirrors
 * `stampedGroundTruthFilename` (uniformity, A.19). The base is the source doc name
 * with any trailing extension stripped.
 */
export function stampedCritiqueFilename(sourceName, now) {
    const base = (sourceName ?? "document").replace(/\.[A-Za-z0-9]+$/, "") || "document";
    const stamp = pad2(now.getFullYear() % 100) + pad2(now.getMonth() + 1) + pad2(now.getDate())
        + "." + pad2(now.getHours()) + pad2(now.getMinutes()) + pad2(now.getSeconds());
    return `${base}-critique-${stamp}.json`;
}
/**
 * Promote a train-partition record to `frozen` — the frozen set is a closed,
 * monotonically-growing tuple (A.5 / rule #17): records enter only by explicit
 * promotion, never silently, and are never demoted. Append-only (§3.3): this returns
 * a NEW record value rather than mutating its input, so the training history is
 * preserved as a superseding record.
 */
export function promoteToFrozen(record) {
    return { ...record, partition: "frozen" };
}
/** Partition every record in a corpus into its train / frozen buckets (stable order). */
export function partitionCorpus(records) {
    const train = [];
    const frozen = [];
    for (const r of records) {
        if (r.partition === "frozen")
            frozen.push(r);
        else
            train.push(r);
    }
    return { train, frozen };
}
